import { IBEError } from '@common/interfaces/be-error.interface';
import { ICollectionRequestOptions } from '@common/interfaces/collection-request-options.interface';
import { ICollection } from '@common/interfaces/collection.interface';
import { UseQueryOptions } from '@common/interfaces/query-options.interface';
import { createCollection } from '@common/utils/create-collection.util';
import { getSectorFromPostCode } from '@common/utils/get-sector-from-post-code.util';
import { createDeliveryConfigDto, IDeliveryConfigDto } from '@src/api/model/delivery-config.dto';
import {
    packHouseApiRequest,
    TPackhouseV1DeliveryConfigurationGetError,
    TPackhouseV1DeliveryConfigurationIdGetError,
} from '@src/api/pack-house-generated-api';
import { queryClient } from '@src/shared/query-client';
import { useQuery, UseQueryResult } from 'react-query';

const NAME = 'delivery-configuration';
const useGetDeliveryConfigurationList = (
    query: ICollectionRequestOptions | undefined,
    queryOptions?: UseQueryOptions,
): UseQueryResult<ICollection<IDeliveryConfigDto>, IBEError<TPackhouseV1DeliveryConfigurationGetError>> => {
    return useQuery<ICollection<IDeliveryConfigDto>, IBEError<''>>(
        `${NAME}-list`,
        () => {
            return packHouseApiRequest.packhouseV1DeliveryConfigurationGet(query).then((r) =>
                createCollection((r.data.list || []).map((e) => createDeliveryConfigDto(e)) || [], 0, {
                    limit: Number.MAX_VALUE,
                }),
            );
        },
        queryOptions,
    );
};

const useGetDeliveryConfigurationById = (
    id: string,
    queryOptions?: UseQueryOptions,
): UseQueryResult<IDeliveryConfigDto, IBEError<TPackhouseV1DeliveryConfigurationIdGetError>> => {
    return useQuery<IDeliveryConfigDto, IBEError<TPackhouseV1DeliveryConfigurationIdGetError>>(
        `${NAME}-${id}`,
        () => {
            return packHouseApiRequest
                .packhouseV1DeliveryConfigurationIdGet(id)
                .then((r) => createDeliveryConfigDto(r.data));
        },
        queryOptions,
    );
};

const useGetDeliveryConfigurationListByPostCode = (
    postCode: string,
    queryOptions?: UseQueryOptions,
): UseQueryResult<ICollection<IDeliveryConfigDto>, IBEError<TPackhouseV1DeliveryConfigurationGetError>> => {
    return useQuery<ICollection<IDeliveryConfigDto>, IBEError<''>>(
        `${NAME}-list-${postCode}`,
        () => {
            const sector = getSectorFromPostCode(postCode);
            return packHouseApiRequest
                .packhouseV1DeliveryConfigurationGet({ postcode: sector })
                .then((r) => createCollection((r.data.list || []).map((e) => createDeliveryConfigDto(e)) || [], 0, {}));
        },
        queryOptions,
    );
};

const getDeliveryConfigsFromCache = (): ICollection<IDeliveryConfigDto> | undefined => {
    return queryClient.getQueryData(`${NAME}-list`);
};

export const deliveryConfiguration = {
    useGetDeliveryConfigurationList,
    useGetDeliveryConfigurationById,
    useGetDeliveryConfigurationListByPostCode,
    getDeliveryConfigsFromCache,
};
